import React, { memo, useMemo, useRef, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Card } from 'primereact/card'

import { SubscriptionCardWidget } from '../subscription-card-widget/subscription-card-widget'
import type { BillingInterval } from '../subscription-card-widget/subscription-card-widget-model'
import {
    PaymentCreateIndividualCheckoutSessionApiArg,
    PaymentCreateOrganizationCheckoutSessionApiArg,
    PaymentBillableConnectorsSummary,
    PaymentOrganizationSubscriptionRequest,
    PaymentProductDisplayResponse,
    PaymentProductsDisplayResponse,
    usePaymentCreateIndividualCheckoutSessionMutation,
    usePaymentCreateOrganizationCheckoutSessionMutation,
} from '../../../common/redux/api/web-api'
import { BasicSubscriptionCardWidget } from '../basic-subscription-card-widget'
import useText from '../../../common/hooks/useText'
import { useToast } from '../../../common/uikit/ToastProvider'
import { useLocalizationContext } from '../../../common/components/localization-provider'

import { BillingPaymentSetupDialog, InvoicePaymentDetails } from './dialogs/billing-payment-setup-dialog'
import {
    buildSubscriptionMarketingFeatures,
    getCommercialOfferingsCurrency,
    getBillableConnectorQuantity,
    getProductPrice,
    SelectedSubscriptionOffering,
    supportsInvoiceDiscountCode,
    toStripeLocale,
} from './billing-subscriptions-widget-model'

export type BillingSubscriptionsWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    commercialOfferings: PaymentProductsDisplayResponse
    billableConnectors?: PaymentBillableConnectorsSummary | null
    companyId: string | null
    communicationLanguage?: string | null
    isCommunicationLanguageLoading: boolean
    onCreateOrganizationSubscription(request: PaymentOrganizationSubscriptionRequest): Promise<void>
}

const EMPTY_PRODUCTS: PaymentProductDisplayResponse[] = []

export const BillingSubscriptionsWidget = memo(function BillingSubscriptionsWidget({
    className,
    style,
    commercialOfferings,
    billableConnectors,
    companyId,
    communicationLanguage,
    isCommunicationLanguageLoading,
    onCreateOrganizationSubscription,
}: BillingSubscriptionsWidgetProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const { locale } = useLocalizationContext()

    const idemKeyRefInvoicePayment = useRef<string | null>(null)
    const idemKeyRefCardPayment = useRef<string | null>(null)

    const [selectedOffering, setSelectedOffering] = useState<SelectedSubscriptionOffering | null>(null)
    const [isCreatingSubscription, setIsCreatingSubscription] = useState(false)
    const [isLoadingCheckoutSession, setIsLoadingCheckoutSession] = useState(false)
    const [createCheckoutSessionOrg] = usePaymentCreateOrganizationCheckoutSessionMutation()
    const [createCheckoutSessionInd] = usePaymentCreateIndividualCheckoutSessionMutation()

    const openPaymentSetup = (product: PaymentProductDisplayResponse, billingInterval: BillingInterval) =>
        setSelectedOffering({ product, price: getProductPrice(product, billingInterval) })

    const closePaymentSetup = () => {
        setSelectedOffering(null)
        idemKeyRefInvoicePayment.current = null
        idemKeyRefCardPayment.current = null
    }

    const handleSubmitInvoice = async (data: InvoicePaymentDetails) => {
        if (!selectedOffering || !companyId || isCreatingSubscription || isCommunicationLanguageLoading) return

        if (!idemKeyRefInvoicePayment.current) idemKeyRefInvoicePayment.current = crypto.randomUUID()

        setIsCreatingSubscription(true)

        try {
            await onCreateOrganizationSubscription({
                ...data,
                preferredLocale: communicationLanguage ?? '',
                productId: selectedOffering.product.id,
                productPriceVersionId: selectedOffering.price.productPriceVersionId,
                idempotencyKey: idemKeyRefInvoicePayment.current,
            })
        } finally {
            setIsCreatingSubscription(false)
            idemKeyRefInvoicePayment.current = null
        }
    }

    const handleSubmitCard = async (type: 'individual' | 'organization') => {
        if (!selectedOffering || isCommunicationLanguageLoading) return

        if (!idemKeyRefCardPayment.current) idemKeyRefCardPayment.current = crypto.randomUUID()

        try {
            setIsLoadingCheckoutSession(true)

            const stripeCheckoutLocale = toStripeLocale(locale)
            const stripeCustomerPreferredLocale = toStripeLocale(communicationLanguage)
            const checkoutSessionPayload = {
                productId: selectedOffering.product.id,
                productPriceVersionId: selectedOffering.price.productPriceVersionId,
                idempotencyKey: idemKeyRefCardPayment.current,
                ...(stripeCheckoutLocale ? { preferredLocale: stripeCheckoutLocale } : {}),
                ...(stripeCustomerPreferredLocale ? { customerPreferredLocale: stripeCustomerPreferredLocale } : {}),
            }

            const res =
                type === 'organization'
                    ? await createCheckoutSessionOrg({
                          paymentCheckoutSessionRequest: checkoutSessionPayload,
                      } satisfies PaymentCreateOrganizationCheckoutSessionApiArg).unwrap()
                    : await createCheckoutSessionInd({
                          paymentIndividualCheckoutSessionRequest: checkoutSessionPayload,
                      } satisfies PaymentCreateIndividualCheckoutSessionApiArg).unwrap()

            if (res.url) window.location.href = res.url
        } catch {
            toast.notifyError(text('lblCouldNotCreateCheckoutSession'))
        } finally {
            setIsLoadingCheckoutSession(false)
            idemKeyRefCardPayment.current = null
        }
    }

    const products = commercialOfferings.products ?? EMPTY_PRODUCTS
    const currency = getCommercialOfferingsCurrency(commercialOfferings)
    const marketingFeatures = useMemo(() => buildSubscriptionMarketingFeatures(products), [products])

    const selectedProductBillableConnectorQuantity = selectedOffering
        ? getBillableConnectorQuantity(billableConnectors, selectedOffering.product)
        : undefined
    const isCardPaymentDisabled = selectedOffering ? selectedProductBillableConnectorQuantity === 0 : false
    const isInvoiceDiscountCodeEnabled = selectedOffering ? supportsInvoiceDiscountCode(selectedOffering) : false

    const subscriptionCards = products.map((product) => (
        <div key={product.id} className="col-12 md:col-6 xl:col-4">
            <SubscriptionCardWidget
                product={product}
                currency={currency}
                billableConnectors={billableConnectors}
                onSelectPlan={(billingInterval) => openPaymentSetup(product, billingInterval)}
                marketingFeatures={marketingFeatures}
            />
        </div>
    ))

    return (
        <Card>
            <div className={cn(className)} style={style}>
                <div className="flex flex-column gap-3">
                    <div className="flex grid justify-content-center">
                        <div className="col-12 md:col-6 xl:col-4">
                            <BasicSubscriptionCardWidget marketingFeatures={marketingFeatures} currency={currency} />
                        </div>
                        {subscriptionCards}
                    </div>

                    <div className="flex align-items-center justify-content-center text-center text-sm text-gray-500 flex-column">
                        <p className="m-0 flex gap-1">
                            {billableConnectors
                                ? products.map((product) => (
                                      <span key={product.id}>
                                          {text('lblProductPriceBasedOnConnectors', {
                                              product: product.name,
                                              connectorCount: getBillableConnectorQuantity(billableConnectors, product),
                                          })}
                                      </span>
                                  ))
                                : null}
                            <span>{text('lblIncludingVat')}</span>
                        </p>
                    </div>
                </div>
            </div>

            {selectedOffering ? (
                <BillingPaymentSetupDialog
                    visible
                    onHide={closePaymentSetup}
                    productId={selectedOffering.product.id}
                    priceId={selectedOffering.price.productPriceVersionId}
                    administrativeFee={commercialOfferings.administrativeFee}
                    currency={currency}
                    onSubmitInvoice={handleSubmitInvoice}
                    onSubmitCard={handleSubmitCard}
                    isLoadingCheckoutSession={isLoadingCheckoutSession || isCommunicationLanguageLoading}
                    isCreatingSubscription={isCreatingSubscription}
                    isCardPaymentDisabled={isCardPaymentDisabled}
                    isInvoiceDiscountCodeEnabled={isInvoiceDiscountCodeEnabled}
                    discountCodeQuantity={selectedProductBillableConnectorQuantity}
                />
            ) : null}
        </Card>
    )
})
